import React, { useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import Header from './components/Header';
import { ntvImages } from '../Theming/images';
import { useNavigate } from 'react-router-dom';
import {
  ANALYTICS_ACTION_LABELS,
  ANALYTICS_PAGE_NAMES,
  trackActivationActionEvent,
  useIsIPad,
} from '../Common/Analytics/utils';
import { usePageViewTracking, useQuery } from '../Common/Hooks';
import { getAnalytics, logEvent } from 'firebase/analytics';
import app from '../Firebase';
import { Path } from '../Routing';

const Container = styled.div<{ isIPad?: boolean }>`
  position: relative;
  flex: 1;
  flex-direction: column;
  display: flex;
  max-height: 100dvh;
  width: 100vw;
  margin: 0 auto;
`;

const MainContent = styled.div<{ isIpad?: boolean }>`
  padding: ${({ isIpad }) => (isIpad ? '32px 200px' : '32px 24px')};

  & > p {
    text-align: center;
    font-size: ${({ isIpad }) => (isIpad ? '24px' : '18px')};
  }

  & > img {
    width: ${({ isIpad }) => (isIpad ? '75%' : '100%')};
    ${({ isIpad }) =>
      isIpad &&
      `
      display: block;
      margin: 0 auto;
    `}
  }
`;

const bounce = keyframes`
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-20px);
  }
  60% {
    transform: translateY(-10px);
  }
`;

const StyledImage = styled.img`
  position: absolute;
  top: 50px;
  right: 50px;
  animation: ${bounce} 2s infinite;
  width: 230px;
  height: 47px;
  z-index: 10;
`;

interface Message {
  data: string & { type: string; givenPermission?: boolean };
}

const NTVPluginActivationAlwaysAllow = () => {
  const navigate = useNavigate();
  const { additional_permissions_page } = ANALYTICS_PAGE_NAMES;
  const { activation_permissions } = ANALYTICS_ACTION_LABELS;
  usePageViewTracking(additional_permissions_page, 'NTV');
  const query = useQuery();

  const isIPad = useIsIPad();

  useEffect(() => {
    const analytics = getAnalytics(app);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const messageHandler = (message: Message) => {
      if (message && message.data === '') {
        window.location.reload();
      } else if (message) {
        switch (message.data?.type) {
          case 'PluginEnabled': {
            const permissionType = message.data?.givenPermission
              ? 'full'
              : 'partial';

            // Mark that user has activated the plugin at least once
            localStorage.setItem('ntv_plugin_activated', 'true');
            // Store the permission type they granted
            localStorage.setItem('ntv_permission_type', permissionType);

            trackActivationActionEvent(
              query,
              'NTV',
              activation_permissions,
              additional_permissions_page,
              {
                permissions: message.data?.givenPermission ? 'Full' : 'Partial',
              }
            );
            logEvent(analytics, 'activation_permissions', {
              permissions: message.data?.givenPermission ? 'Full' : 'Partial',
              partner: 'NTV',
            });

            if (message.data?.givenPermission) {
              navigate({
                pathname: Path.NTVPluginActivationStartShopping,
                search: location.search,
              });
            } else {
              navigate({
                pathname: Path.NTVPluginActivationPartialPermissions,
                search: location.search,
              });
            }
            break;
          }
        }
      }
    };

    window.addEventListener('message', messageHandler);

    return () => window.removeEventListener('message', messageHandler);
  }, [navigate]);
  return (
    <Container isIPad={isIPad}>
      <StyledImage src={ntvImages.safari_tap_review} />

      <Header
        logoWidth={51}
        height={320}
        showFAQ
        text={
          <>
            <span>Fast geschafft: Klicken Sie</span>
            <span>oben auf “Überprüfen”.</span>
          </>
        }
      />
      <MainContent isIpad={isIPad}>
        <p>
          Wählen Sie <strong>„Immer erlauben“</strong> und dann
          <strong> “für alle Webseiten”,</strong> sonst können wir keine
          passenden Rabattcodes für Sie finden.
        </p>
        <img src={ntvImages.safari_always_allow} alt="Safari Always Allow" />
      </MainContent>
    </Container>
  );
};

export default NTVPluginActivationAlwaysAllow;
